/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/languageApi.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    API functions for Languages.
    Centralizes all API calls related to Languages.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  Language,
  LanguagePayload,
  ListLanguagesResponse,
} from '../types';

export const LANGUAGES_ENDPOINT = '/api/telephony/languages';

/**
 * Generic API request helper
 */
async function requestLanguagesApi<T>(
  endpoint: string,
  errorMessage: string,
  options?: RequestInit,
): Promise<{ data: T }> {
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    let message = errorMessage;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || errorMessage);
  }

  return { data: result.data };
}

/**
 * List Languages with pagination and search
 */
export async function fetchLanguages(
  pagination: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    domainId?: number;
  },
  search?: string,
  sortBy?: string,
): Promise<ListLanguagesResponse> {
  const queryParams = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: sortBy || pagination.sortBy,
    sortOrder: pagination.sortOrder,
  });

  if (search) {
    queryParams.append('search', search);
  }

  if (pagination.domainId != null && !Number.isNaN(Number(pagination.domainId))) {
    queryParams.append('domainId', pagination.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`${LANGUAGES_ENDPOINT}?${queryParams}`),
    {
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch languages');
  }

  const result = await response.json();

  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error(result?.message || 'Invalid response');
  }

  return {
    success: true,
    data: result.data as Language[],
    pagination: {
      page: result.pagination?.page || pagination.page,
      limit: result.pagination?.limit || pagination.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || 0,
    },
  };
}

/**
 * Get single Language by ID
 */
export async function fetchLanguage(id: string | number): Promise<Language> {
  const { data } = await requestLanguagesApi<Language>(
    `${LANGUAGES_ENDPOINT}/${id}`,
    'Failed to fetch language',
  );
  return data;
}

/**
 * Create new Language
 */
export async function createLanguage(
  payload: LanguagePayload,
): Promise<Language> {
  const { data } = await requestLanguagesApi<Language>(
    LANGUAGES_ENDPOINT,
    'Failed to create language',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Update Language
 */
export async function updateLanguage(
  id: string | number,
  payload: LanguagePayload,
): Promise<Language> {
  const { data } = await requestLanguagesApi<Language>(
    `${LANGUAGES_ENDPOINT}/${id}`,
    'Failed to update language',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Delete Language
 */
export async function deleteLanguage(id: string | number): Promise<void> {
  await requestLanguagesApi<null>(
    `${LANGUAGES_ENDPOINT}/${id}`,
    'Failed to delete language',
    {
      method: 'DELETE',
    },
  );
}

